import React, { useCallback, useMemo } from 'react';
import { di } from 'react-magnetic-di';
import type { ComponentMap } from '@atlassian/forge-ui';
import { ExtensionPointProvider } from '@atlassian/help-center-common-component/forge-ui/extension-point-provider';
import { ExtensionProvider } from '@atlassian/help-center-common-component/forge-ui/extension-provider';
import { Renderer } from '@atlassian/help-center-common-component/forge-ui/renderer';
import { TopLevelComponent } from '@atlassian/help-center-common-component/forge-ui/top-level-component';
import type {
    CustomField,
    CustomFieldType,
    ExtensionData,
    ExtensionPayload,
} from '@atlassian/help-center-common-component/forge-ui/types';

export interface Props {
    extension: CustomField | CustomFieldType;
    extensionData: ExtensionData;
    extensionPayload: ExtensionPayload;
    loadingComponent: React.ReactNode;
    onSubmit: (fieldValue: unknown) => void;
}

export const ForgeCustomFieldEdit = ({
    extension,
    extensionData,
    extensionPayload,
    loadingComponent,
    onSubmit,
}: Props) => {
    di(ExtensionPointProvider, ExtensionProvider, Renderer);

    const getComponents = useCallback(
        (defaults: ComponentMap) => ({
            ...defaults,
            CustomField: TopLevelComponent,
        }),
        []
    );

    const bridge = useMemo(
        () => ({
            submit: ({ data }: { data: unknown }): undefined => {
                Promise.resolve(data)
                    .then(onSubmit)
                    .catch(() => {
                        // TODO: handle error https://ecosystem-platform.atlassian.net/browse/ARKEN-2206
                    });
            },
        }),
        [onSubmit]
    );

    return (
        <ExtensionPointProvider extensionType={extension.type}>
            <ExtensionProvider extension={extension}>
                <Renderer
                    extension={extension}
                    extensionData={extensionData}
                    extensionPayload={extensionPayload}
                    getComponents={getComponents}
                    entryPoint="edit"
                    bridge={bridge}
                    loadingComponent={loadingComponent as JSX.Element}
                />
            </ExtensionProvider>
        </ExtensionPointProvider>
    );
};
